/**
 * Frontend Medicare card-number validation — immediate practitioner UX feedback.
 *
 * B1.2B: aligned with the authoritative backend persistence guard
 * (authentication: App\Services\HealthcareIdentifiers\MedicareCardNumberValidator),
 * which implements the Healthcare Identifiers Service Appendix D
 * "Medicare Card No. Check Algrthm".
 *
 * A full representation contains ten digits:
 *   - digits 1-8: base number;
 *   - digit 9:    check digit;
 *   - digit 10:   issue number (excluded from the checksum).
 *
 * Only deliberately supported display whitespace (spaces and tabs, used when a
 * number is shown grouped, e.g. "2123 45670 1") is removed. Other non-digit
 * content (letters or punctuation) is rejected rather than silently stripped, so
 * this helper accepts/rejects the same representations as the backend guard. The
 * canonical API path still submits the continuous-digit form.
 */
export const validateMedicareNumber = (medicareNumber: string): { isValid: boolean; error?: string } => {
	// Remove only supported display whitespace: trim surrounding whitespace, then
	// drop spaces and tabs used for visual grouping. Any other non-digit content is
	// preserved so it can be rejected below rather than silently "repaired".
	const normalised = medicareNumber.trim().replace(/[ \t]+/g, '');

	// Reject any remaining non-digit content instead of stripping it.
	if (!/^\d*$/.test(normalised)) {
		return { isValid: false, error: 'Medicare number must contain digits only.' };
	}

	// Ten-digit structure: 8 base digits + check digit + issue number.
	if (normalised.length !== 10) {
		return { isValid: false, error: 'Medicare number must be exactly 10 digits.' };
	}

	const digits = normalised.split('').map(Number);

	// First digit must be between 2 and 6 inclusive.
	if (digits[0] < 2 || digits[0] > 6) {
		return { isValid: false, error: 'The first digit must be between 2 and 6.' };
	}

	// Appendix D weighted checksum over the first eight digits. The ninth digit is
	// the check digit; the tenth digit is the issue number and is excluded.
	const mainNumber = digits.slice(0, 8);
	const providedCheckDigit = digits[8];
	const weights = [1, 3, 7, 9, 1, 3, 7, 9];
	const sum = mainNumber.reduce((acc, digit, index) => acc + digit * weights[index], 0);
	const expectedCheckDigit = sum % 10;

	if (providedCheckDigit !== expectedCheckDigit) {
		return { isValid: false, error: `Invalid check digit. Expected ${expectedCheckDigit}.` };
	}

	return { isValid: true };
};
